import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { BrandPlan, BrandPlanTactic, ContentCount } from 'types'

type TacticProviderMap = ReadonlyArray<{ tactic: BrandPlanTactic; provider: ExternalIntegrationEnum }>

const providersForTactics = (
  tactics: BrandPlanTactic[],
  tacticToProvider: TacticProviderMap,
): ExternalIntegrationEnum[] => {
  const unique = new Set<ExternalIntegrationEnum>()
  for (const tactic of tactics) {
    const entry = tacticToProvider.find((t) => t.tactic === tactic)
    if (entry) unique.add(entry.provider)
  }
  return Array.from(unique)
}

const hasAnyContent = (contents: ContentCount[] | undefined): boolean =>
  !!contents?.some((entry) => (entry.count ?? 0) > 0)

/**
 * SEO and Email can't be removed from `tactics` in the UI, so a plan may list
 * them even when the user has zeroed out every row in `seo_contents` /
 * `email_contents`. Treat those as absent — they shouldn't demand a connection
 * or gate other rules.
 */
export const effectiveTacticsForPlan = (plan: BrandPlan | null | undefined): BrandPlanTactic[] => {
  if (!plan) return []
  return plan.tactics.filter((tactic) => {
    if (tactic === BrandPlanTactic.SEO) return hasAnyContent(plan.seo_contents)
    if (tactic === BrandPlanTactic.Email) return hasAnyContent(plan.email_contents)
    return true
  })
}

/**
 * Providers that the brand plan's tactics need, but that the workspace has not
 * yet connected. Returns [] when either input is missing, so the caller can
 * treat `length === 0` as "everything needed is connected".
 */
export const missingProvidersForTactics = (
  tactics: BrandPlanTactic[] | undefined,
  integrations: ExternalIntegration[] | undefined,
  tacticToProvider: TacticProviderMap,
): ExternalIntegrationEnum[] => {
  if (!tactics || !integrations) return []
  const needed = providersForTactics(tactics, tacticToProvider)
  const connected = new Set(integrations.map((i) => i.provider))
  return needed.filter((p) => !connected.has(p))
}
